import { BrowserRouter, Routes, Route, Navigate, useInRouterContext } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ModulePathProvider, useModulePath } from './context/ModulePathContext';
import './styles/shared.css';

import Home from './pages/Home';
import SubmitChange from './pages/SubmitChange';
import ManageChange from './pages/ManageChange';
import AmendChange from './pages/AmendChange';
import ChangeDetail from './pages/ChangeDetail';
import PlanSignoff from './pages/PlanSignoff';
import ReviewApprove from './pages/ReviewApprove';
import StakeholderResponse from './pages/StakeholderResponse';
import Survey from './pages/Survey';
import SponsorClosure from './pages/SponsorClosure';

interface AppProps {
  // Still passed by the ERP shell's ModuleLoader when federated, kept here for prop-shape
  // compatibility — unused, since every API call authenticates via the shared cookie, not a token.
  token?: string;
  user: { name: string; email: string };
  roles: string[];
  basePath?: string;
  refreshToken: () => Promise<void>;
}

function AppRoutes() {
  const basePath = useModulePath();

  return (
    <Routes>
      <Route path='/' element={<Home />} />
      <Route path='/submit-change' element={<SubmitChange />} />
      <Route path='/manage-change' element={<ManageChange />} />
      <Route path='/change-detail' element={<ChangeDetail />} />
      <Route path='/amend-change' element={<AmendChange />} />

      <Route path='/plan-signoff' element={<PlanSignoff />} />
      <Route path='/review-approve' element={<ReviewApprove />} />
      <Route path='/survey' element={<Survey />} />
      <Route path='/sponsor-closure' element={<SponsorClosure />} />
      <Route path='/stakeholder-response' element={<StakeholderResponse />} />

      <Route path='*' element={<Navigate to={basePath || '/'} replace />} />
    </Routes>
  );
}

export default function App({ user, roles, basePath, refreshToken }: AppProps) {
  // Federated into a host that already runs a BrowserRouter: render routes
  // directly so they resolve relative to wherever the host mounted us.
  // Standalone (no outer router, e.g. main.tsx): own the BrowserRouter.
  const hasOuterRouter = useInRouterContext();

  return (
    <AuthProvider user={user} roles={roles} refreshToken={refreshToken}>
      <ToastProvider>
        <ModulePathProvider value={basePath ?? ''}>
          {hasOuterRouter ? (
            <AppRoutes />
          ) : (
            <BrowserRouter basename={basePath}>
              <AppRoutes />
            </BrowserRouter>
          )}
        </ModulePathProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
